import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import toast, { Toaster } from 'react-hot-toast';
import { addTowishlist } from '../features/products/productSlice';
import { getUserCart, addProdToCart, syncLocalCart } from '../features/user/userSlice';
import { base_url } from '../utils/axiosConfig';
import axios from 'axios';
import { IoMdCart } from "react-icons/io";
import { FaCartShopping, FaArrowRight } from "react-icons/fa6";
import { CiHeart } from "react-icons/ci";
import { IoMdAdd, IoMdFlash, IoMdClose } from "react-icons/io";
import { FaHeart, FaTrashAlt, FaCheckCircle, FaTimesCircle, FaMinus, FaPlus } from "react-icons/fa";

const showToast = (type, message, subtext) => {
  const isSuccess = type === 'success';
  toast.custom((t) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        background: '#fff',
        border: `1px solid ${isSuccess ? '#d1fae5' : '#fee2e2'}`,
        borderLeft: `4px solid ${isSuccess ? '#10b981' : '#ef4444'}`,
        borderRadius: '12px',
        padding: '12px 16px',
        minWidth: '260px',
        maxWidth: '340px',
        opacity: t.visible ? 1 : 0,
        transform: t.visible ? 'translateY(0)' : 'translateY(-12px)',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
      }}
      onClick={() => toast.dismiss(t.id)}
    >
      <div style={{
        width: 36, height: 36, borderRadius: '50%',
        background: isSuccess ? '#d1fae5' : '#fee2e2',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
      }}>
        {isSuccess
          ? <FaCheckCircle color="#10b981" size={18} />
          : <FaTimesCircle color="#ef4444" size={18} />}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: '14px', color: '#111', lineHeight: 1.3 }}>{message}</div>
        {subtext && <div style={{ fontSize: '11px', color: '#6b7280', marginTop: 2 }}>{subtext}</div>}
      </div>
    </div>
  ), { duration: 2500 });
};

const ProductCard = ({ grid, data }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(data?.variants?.[0] || null);

  const { isAuthenticated, cartProducts, wishlist, user } = useSelector((state) => state.auth);

  const productId = data?._id || '';
  const variantId = selectedVariant?._id;
  const variantStr = selectedVariant?.measurementUnit;
  const cartItem = isAuthenticated
    ? cartProducts.find(item => item?.ProductId?._id === productId && (item.variantId === variantId || item.variant === variantStr))
    : (JSON.parse(localStorage.getItem('cart')) || []).find(item => item?.ProductId?._id === productId && (item.variantId === variantId || item.variant === variantStr));

  const totalQty = (isAuthenticated ? cartProducts : (JSON.parse(localStorage.getItem('cart')) || []))
    .filter(item => item?.ProductId?._id === productId)
    .reduce((sum, item) => sum + (item.quantity || 0), 0);

  const [quantity, setQuantity] = useState(cartItem?.quantity || 1);
  const [alreadyAdded, setAlreadyAdded] = useState(!!cartItem);
  const [isWishlisted, setIsWishlisted] = useState(
    Array.isArray(wishlist) && wishlist.length > 0
      ? wishlist.some(item => item?._id === productId)
      : false
  );
  const [cartItems, setCartItems] = useState(
    isAuthenticated ? cartProducts : JSON.parse(localStorage.getItem('cart')) || []
  );

  // 🔹 Get role (either from redux or localStorage)
  const role = user?.role || JSON.parse(localStorage.getItem('customer'))?.role;
  const [showModal, setShowModal] = useState(false);
  const [modalError, setModalError] = useState(null);

  useEffect(() => {
    setAlreadyAdded(!!cartItem);
    setQuantity(cartItem?.quantity || 1);
    setCartItems(isAuthenticated ? cartProducts : JSON.parse(localStorage.getItem('cart')) || []);
    setIsWishlisted(
      Array.isArray(wishlist) && wishlist.length > 0
        ? wishlist.some(item => item?._id === productId)
        : false
    );
    if (!selectedVariant && data?.variants?.length > 0) {
      setSelectedVariant(data.variants[0]);
    }
  }, [cartItem, wishlist, productId, isAuthenticated, cartProducts, data, selectedVariant]);

  if (!data || !data._id) {
    console.warn("ProductCard received invalid data:", data);
    return null;
  }

  const currentPrice = selectedVariant ? selectedVariant.CurrentPrice : data.CurrentPrice;
  const offerPrice = selectedVariant ? selectedVariant.offerPrice : data.offerPrice;

  const finalPrice = offerPrice && offerPrice > 0 ? offerPrice : currentPrice;
  const gstPercent = parseFloat(data.gst || '0');
  const gstAmount = (finalPrice * gstPercent) / 100;
  const isOutOfStock = selectedVariant ? (selectedVariant.quantity <= 0) : (data.quantity <= 0);

  // 🔹 Wishlist
  const addToWish = async (id, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!isAuthenticated) {
      toast('Please Login to add to wishlist', { icon: '🔐' });
      return;
    }

    if (role === "deliveryboy") {
      showToast('error', 'Action Restricted', 'Delivery boys cannot add products to wishlist.');
      return;
    }

    try {
      await dispatch(addTowishlist(id)).unwrap();
      const wasWishlisted = isWishlisted;
      setIsWishlisted(!isWishlisted);
      showToast('success',
        wasWishlisted ? 'Removed from Wishlist' : 'Added to Wishlist',
        wasWishlisted ? 'Product removed successfully' : 'Product saved for later'
      );
    } catch (error) {
      console.log(error);
      showToast('error', 'Update Failed', 'Failed to update wishlist. Please try again.');
    }
  };

  // 🔹 Handle Cart Button Click (Main Card)
  const handleCartButtonClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setModalError(null);
    setShowModal(true);
  };

  // 🔹 Add to cart with sparks (Inside Modal)
  const handleAddToCartWithSpark = (e) => {
    handleAddToCart();

    const parent = e.currentTarget.parentElement;
    if (!parent) return;

    for (let i = 0; i < 10; i++) {
      const spark = document.createElement('span');
      spark.className = 'fire-spark';
      const angle = Math.random() * 4 * Math.PI;
      const distance = 80 + Math.random() * 20;
      const x = Math.cos(angle) * distance;
      const y = Math.sin(angle) * distance;

      spark.style.setProperty('--x', `${x}px`);
      spark.style.setProperty('--y', `${y}px`);
      parent.appendChild(spark);
      setTimeout(() => spark.remove(), 1000);
    }
  };

  // 🔹 Add to Cart
  const handleAddToCart = async () => {
    if (role === "deliveryboy") {
      showToast('error', 'Action Restricted', 'Delivery boys cannot add products to cart.');
      return;
    }

    setLoading(true);

    const variantId = selectedVariant?._id;
    const variantStr = selectedVariant?.measurementUnit || "";

    if (!isAuthenticated) {
      const localCart = JSON.parse(localStorage.getItem('cart')) || [];
      const existingIndex = localCart.findIndex(item =>
        item.ProductId._id === data._id &&
        (item.variantId === variantId || item.variant === variantStr)
      );

      if (existingIndex !== -1) {
        localCart[existingIndex].quantity += quantity;
      } else {
        localCart.push({
          _id: `${data._id}_${variantId}_${Date.now()}`,
          ProductId: {
            _id: data._id,
            title: data.title,
            images: data.images,
            price: finalPrice,
            originalPrice: currentPrice,
            offerPrice: offerPrice,
            gst: data.gst || '0',
          },
          variantId,
          variant: variantStr,
          measurementUnit: variantStr,
          quantity,
          price: finalPrice,
          gst: data.gst || '0',
        });
      }

      localStorage.setItem('cart', JSON.stringify(localCart));
      dispatch(syncLocalCart(localCart));
      setCartItems(localCart);
      showToast('success', 'Added to cart!', `${data?.title?.slice(0, 30)}${data?.title?.length > 30 ? '…' : ''}`);
      setAlreadyAdded(true);
      setLoading(false);
      return;
    }

    try {
      const variantId = selectedVariant?._id;
      const variantStr = selectedVariant?.measurementUnit || "";
      const payload = {
        ProductId: {
          _id: data._id,
          title: data.title,
          images: data.images,
          price: finalPrice,
          originalPrice: currentPrice,
          offerPrice: offerPrice,
          gst: data.gst || '0',
        },
        variantId,
        variant: variantStr,
        measurementUnit: variantStr,
        quantity: cartItem ? cartItem.quantity + quantity : quantity,
        price: finalPrice,
      };
      await dispatch(addProdToCart(payload)).unwrap();
      dispatch(getUserCart());
      showToast('success', 'Added to cart!', `${data?.title?.slice(0, 30)}${data?.title?.length > 30 ? '…' : ''}`);
      setAlreadyAdded(true);
    } catch (err) {
      const errorMessage = err?.message || err?.response?.data?.message || err || 'Failed to add product. Please try again.';
      alert(`Error: ${errorMessage}`);
      showToast('error', 'Failed to add', errorMessage);
      if (showModal) {
        setModalError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Buy Now
  const handleBuyNow = async () => {
    if (role === "deliveryboy") {
      showToast('error', 'Action Restricted', 'Delivery boys cannot purchase products.');
      return;
    }

    setLoading(true);
    await handleAddToCart();
    navigate("/cart");
    setLoading(false);
  };

  // 🔹 Local quantity update

  const updateQuantityVariantAPI = async (cartItemId, vId, vStr, vPrice, newQty) => {
    try {
      const token = JSON.parse(localStorage.getItem('customer'))?.token;
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.put(`${base_url}user/updatecart`, {
        cartItemId,
        ProductId: data._id,
        variantId: vId,
        variant: vStr,
        quantity: newQty,
        price: vPrice,
      }, config);

      dispatch(getUserCart());

    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to update quantity. Please try again.';
      showToast('error', 'Update Failed', errorMessage);
      if (showModal) {
        setModalError(errorMessage);
      }
    }
  };

  const updateQuantityVariantLocal = (vId, vStr, newQty) => {
    const localCart = JSON.parse(localStorage.getItem('cart')) || [];
    const updated = localCart.map(item =>
      (item.ProductId._id === data._id && (item.variantId === vId || item.variant === vStr)) ? { ...item, quantity: newQty } : item
    );
    localStorage.setItem('cart', JSON.stringify(updated));
    dispatch(syncLocalCart(updated));
    setCartItems(updated);
  };

  const handleAddVariantToCart = async (variant, price) => {
    if (role === "deliveryboy") {
      showToast('error', 'Action Restricted', 'Delivery boys cannot add products to cart.');
      return;
    }

    setLoading(true);

    const vId = variant?._id;
    const vStr = variant?.measurementUnit || "";
    const unitTitle = variant?.unit?.title || "";     // ← Added

    if (!isAuthenticated) {
      const localCart = JSON.parse(localStorage.getItem('cart')) || [];
      
      const existingIndex = localCart.findIndex(item =>
        item.ProductId._id === data._id && 
        (item.variantId === vId || item.variant === vStr)
      );

      if (existingIndex !== -1) {
        localCart[existingIndex].quantity += 1;
      } else {
        localCart.push({
          _id: `${data._id}_${vId}_${Date.now()}`,
          ProductId: {
            _id: data._id,
            title: data.title, 
            images: data.images, 
            price: price,
            originalPrice: variant.CurrentPrice, 
            offerPrice: variant.offerPrice,
            gst: data.gst || '0',
          },
          variantId: vId, 
          variant: vStr, 
          measurementUnit: vStr,
          unitTitle,                    // ← Added
          quantity: 1, 
          price: price, 
          gst: data.gst || '0',
        });
      }

      localStorage.setItem('cart', JSON.stringify(localCart));
      dispatch(syncLocalCart(localCart));
      setCartItems(localCart);
      
      showToast('success', 'Added to cart!', `${data?.title?.slice(0, 30)}...`);
    } 
    else {
      try {
        const payload = {
          ProductId: {
            _id: data._id, 
            title: data.title, 
            images: data.images, 
            price: price,
            originalPrice: variant.CurrentPrice, 
            offerPrice: variant.offerPrice,
            gst: data.gst || '0',
          },
          variantId: vId, 
          variant: vStr, 
          measurementUnit: vStr,
          unitTitle,                       // ← Added
          quantity: 1, 
          price: price,
        };

        await dispatch(addProdToCart(payload)).unwrap();
        dispatch(getUserCart());
        showToast('success', 'Added to cart!', `${data?.title?.slice(0, 30)}...`);
      } catch (err) {
        const errorMessage = err?.message || err?.response?.data?.message || err || 'Failed to add product. Please try again.';
        alert(`Error: ${errorMessage}`);
        showToast('error', 'Failed to add', errorMessage);
        if (showModal) {
          setModalError(errorMessage);
        }
      }
    }
    
    setLoading(false);
};

  const handleRemoveVariant = async (cartItemId) => {
    if (isAuthenticated) {
      try {
        const token = JSON.parse(localStorage.getItem('customer'))?.token;
        const config = { headers: { Authorization: `Bearer ${token}`, Accept: 'application/json', 'Content-Type': 'application/json' } };
        await axios.delete(`${base_url}user/delete-product-cart`, { ...config, data: { cartItemId } });
        dispatch(getUserCart());
        showToast('success', 'Removed from cart');
      } catch (error) {
        showToast('error', 'Failed to remove');
      }
    } else {
      const localCart = JSON.parse(localStorage.getItem('cart')) || [];
      const updatedCart = localCart.filter(item => item._id !== cartItemId);
      localStorage.setItem('cart', JSON.stringify(updatedCart));
      dispatch(syncLocalCart(updatedCart));
      setCartItems(updatedCart);
      showToast('success', 'Removed from cart');
    }
  };

  const handleRemove = async (cartItemId) => {
    if (isAuthenticated) {
      try {
        const token = JSON.parse(localStorage.getItem('customer'))?.token;
        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        };

        await axios.delete(`${base_url}user/delete-product-cart`, {
          ...config,
          data: { cartItemId },
        });

        const updatedCart = cartProducts.filter(item => item._id !== cartItemId);
        setCartItems(updatedCart);
        showToast('success', 'Removed from cart', 'Item has been removed.');
        dispatch(getUserCart());
        setAlreadyAdded(false);
      } catch (error) {
        console.error('Error removing item:', error);
        showToast('error', 'Failed to remove', 'Please try again.');
      }
    } else {
      const localCart = JSON.parse(localStorage.getItem('cart')) || [];
      const updatedCart = localCart.filter(item => item._id !== cartItemId);
      localStorage.setItem('cart', JSON.stringify(updatedCart));
      setCartItems(updatedCart);
      dispatch(syncLocalCart(updatedCart));
      showToast('success', 'Removed from cart', 'Item has been removed.');
      setAlreadyAdded(false);
    }
  };

  return (
    <div className={`col-6 col-sm-6 col-md-${grid} mb-2`}>
      <div className="card h-100 shadow-sm border-0" style={{ padding: '8px' }}>
        <div
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            addToWish(data._id, e);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              addToWish(data._id, e);
            }
          }}
          className="position-absolute top-0 end-0 m-1 bg-white rounded-circle shadow-sm d-flex align-items-center justify-content-center"
          style={{
            padding: '4px',
            zIndex: 10,
            cursor: 'pointer',
            width: '28px',
            height: '28px'
          }}
        >
          {isWishlisted ? <FaHeart className="text-danger" size={14} /> : <CiHeart size={14} />}
        </div>

        <Link to={`/product/${data._id}`} style={{ textDecoration: 'none' }}>
          <img
            src={`${base_url}${data.images?.[0]?.url}`}
            alt={data.title}
            className="card-img-top"
            style={{ height: '100px', objectFit: 'contain' }}
          />
        </Link>

        <div className="card-body p-1 d-flex flex-column">
          <div className="mb-1" style={{ fontSize: '0.8rem' }}>
            {offerPrice && offerPrice > 0 && offerPrice < currentPrice ? (
              <>
                <strong className="text-success" style={{ fontSize: '15px' }}>
                  ₹{offerPrice?.toLocaleString('en-IN') || '0'}
                </strong>{' '}
                <small className="text-danger" style={{ textDecoration: 'line-through', fontSize: '12px' }}>
                  ₹{currentPrice?.toLocaleString('en-IN') || '0'}
                </small>
                <small className="text-success fw-bold ms-1" style={{ fontSize: '11px' }}>
                  {currentPrice ? Math.round(((currentPrice - (offerPrice || 0)) / currentPrice) * 100) : 0}% OFF
                </small>
              </>
            ) : (
              <strong style={{ fontSize: '15px' }}>₹{currentPrice?.toLocaleString('en-IN') || '0'}</strong>
            )}
            {selectedVariant && (
              <span className="text-muted ms-1" style={{ fontSize: '11px' }}>
                / {selectedVariant.measurementUnit} {selectedVariant.title || selectedVariant.unit?.title || ""}
              </span>
            )}
          </div>

          {/* Removed variant chips from main card as requested */}
          <h6
            className="card-title text-small"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              whiteSpace: 'normal',
              fontSize: '0.85rem',
              lineHeight: '1.2',
              marginBottom: '0.5rem',
            }}
          >
            {data.title}
          </h6>



          <div className="mt-auto d-flex justify-content-between align-items-center">
            {isOutOfStock ? (
              <div className="d-flex gap-1 align-items-center w-100">
                <button
                  disabled
                  className="btn btn-sm text-muted d-flex align-items-center justify-content-center gap-1 w-100"
                  style={{ backgroundColor: "#e5e7eb", border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.75rem', padding: '6px 12px', fontWeight: '600', cursor: 'not-allowed' }}
                >
                  Out of Stock
                </button>
              </div>
            ) : totalQty > 0 ? (
              <div className="d-flex align-items-center gap-1">
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setModalError(null); setShowModal(true); }}
                  className="btn btn-sm text-light d-flex align-items-center gap-1"
                  style={{ backgroundColor: "#10b981", borderRadius: '6px', fontSize: '0.7rem', padding: '5px 10px', fontWeight: '600' }}
                >
                  <FaCheckCircle size={12} /> {totalQty} in Cart
                </button>
              </div>
            ) : (
              <div className="d-flex gap-1 align-items-center w-100">
                <button
                  onClick={handleCartButtonClick}
                  className="btn btn-sm text-light d-flex align-items-center justify-content-center gap-1 w-100"
                  style={{ backgroundColor: "#232f3e", borderRadius: '6px', fontSize: '0.75rem', padding: '6px 12px', fontWeight: '600' }}
                  disabled={loading}
                >
                  <IoMdCart size={14} /> Add to Cart
                </button>
              </div>
            )}
            {totalQty > 0 && (
              <Link to="/cart" className="btn btn-sm btn-outline-success" style={{ fontSize: '0.75rem', padding: '5px 10px', borderRadius: '6px' }}>
                <FaCartShopping size={12} /> <FaArrowRight size={12} />
              </Link>
            )}
          </div>
        </div>

        {/* ── Blinkit-Style Variant Selection Modal ── */}
        {showModal && (
          <div
            className="variant-modal-overlay"
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)',
              zIndex: 10000, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', padding: '20px'
            }}
            onClick={() => { setShowModal(false); setModalError(null); }}
          >
            {/* Outside Close Button */}
            <button
              className="btn d-flex align-items-center justify-content-center mb-3"
              style={{
                width: '40px', height: '40px', borderRadius: '50%',
                backgroundColor: '#000', color: '#fff', border: '2px solid #fff'
              }}
              onClick={() => { setShowModal(false); setModalError(null); }}
            >
              <IoMdClose size={24} />
            </button>

            <div
              className="variant-modal-content"
              style={{
                backgroundColor: '#fff', borderRadius: '24px', width: '100%', maxWidth: '450px',
                maxHeight: '80vh', overflowY: 'auto', position: 'relative',
                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)', animation: 'modalSlideUp 0.3s ease-out',
                padding: '24px'
              }}
              onClick={e => e.stopPropagation()}
            >
              <h5 className="fw-bold mb-4 text-center" style={{ fontSize: '1.1rem', color: '#1f2937' }}>{data.title}</h5>

              {modalError && (
                <div
                  className="alert alert-danger p-2 mb-3 d-flex align-items-center justify-content-between animate__animated animate__fadeIn"
                  style={{ borderRadius: '10px', fontSize: '0.8rem', border: '1px solid #fee2e2', backgroundColor: '#fef2f2', color: '#b91c1c' }}
                >
                  <span style={{ fontWeight: '500' }}>{modalError}</span>
                  <button
                    onClick={() => setModalError(null)}
                    style={{ border: 'none', background: 'transparent', fontSize: '16px', fontWeight: '700', color: '#b91c1c', cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}
                  >
                    ×
                  </button>
                </div>
              )}

              <div className="d-flex flex-column gap-3">
                {data?.variants?.length > 0 ? (
                  data.variants.map((v) => {
                    const vId = v._id;
                    const vStr = v.measurementUnit;
                    const vCartItem = isAuthenticated
                      ? cartProducts.find(item => item?.ProductId?._id === productId && (item.variantId === vId || item.variant === vStr))
                      : (JSON.parse(localStorage.getItem('cart')) || []).find(item => item?.ProductId?._id === productId && (item.variantId === vId || item.variant === vStr));

                    const vInCart = !!vCartItem;
                    const vQty = vCartItem?.quantity || 1;
                    const hasOffer = v.offerPrice !== undefined && v.offerPrice !== null && parseFloat(v.offerPrice) > 0 && parseFloat(v.offerPrice) < parseFloat(v.CurrentPrice);
                    const vPrice = hasOffer ? parseFloat(v.offerPrice) : parseFloat(v.CurrentPrice);
                    const vDiscount = hasOffer ? Math.round(((parseFloat(v.CurrentPrice) - parseFloat(v.offerPrice)) / parseFloat(v.CurrentPrice)) * 100) : 0;

                    return (
                      <div
                        key={vId}
                        className="p-3 d-flex align-items-center justify-content-between"
                        style={{
                          borderRadius: '16px', border: '1px solid #f3f4f6',
                          backgroundColor: '#fff', transition: 'all 0.2s'
                        }}
                      >
                        <div className="d-flex align-items-center gap-3">
                          <div style={{ position: 'relative' }}>
                            <img
                              src={`${base_url}${data.images?.[0]?.url}`}
                              alt={vStr}
                              style={{ width: '64px', height: '64px', objectFit: 'contain', borderRadius: '8px' }}
                            />
                            {vDiscount > 0 && (
                              <div style={{
                                position: 'absolute', top: '-5px', left: '-5px',
                                background: '#2563eb', color: '#fff', fontSize: '10px',
                                fontWeight: '700', padding: '2px 6px', borderRadius: '4px'
                              }}>
                                {vDiscount}% OFF
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="fw-medium text-muted" style={{ fontSize: '0.85rem' }}>{v.measurementUnit} {v.title || v.unit?.title || ""}</div>
                            <div className="d-flex align-items-center gap-2 mt-1">
                              <span className="fw-bold" style={{ color: '#111' }}>₹{vPrice}</span>
                              {hasOffer && (
                                <span className="text-muted text-decoration-line-through" style={{ fontSize: '0.75rem' }}>₹{v.CurrentPrice}</span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div>
                          {v.quantity <= 0 ? (
                            <button
                              disabled
                              className="btn btn-outline-secondary fw-bold px-2 py-1"
                              style={{
                                borderRadius: '8px', fontSize: '0.75rem',
                                color: '#6b7280', borderColor: '#d1d5db',
                                border: '1px solid #d1d5db', backgroundColor: '#f3f4f6',
                                cursor: 'not-allowed'
                              }}
                            >
                              Out of Stock
                            </button>
                          ) : vInCart ? (
                            <div className="d-flex flex-column align-items-end">
                              <div
                                className="d-flex align-items-center justify-content-between"
                                style={{
                                  backgroundColor: '#10b981', color: '#fff',
                                  borderRadius: '8px', width: '90px', padding: '4px'
                                }}
                              >
                                <button
                                  onClick={() => {
                                    // Call update quantity with specific variant data
                                    const newQty = vQty - 1;
                                    if (newQty < 1) {
                                      handleRemoveVariant(vCartItem._id);
                                    } else {
                                      if (isAuthenticated) updateQuantityVariantAPI(vCartItem._id, vId, vStr, vPrice, newQty);
                                      else updateQuantityVariantLocal(vId, vStr, newQty);
                                    }
                                  }}
                                  className="btn btn-sm p-0 border-0 text-white"
                                  style={{ width: '24px' }}
                                >
                                  <FaMinus size={10} />
                                </button>
                                <span className="fw-bold" style={{ fontSize: '0.9rem' }}>{vQty}</span>
                                <button
                                  onClick={() => {
                                    const newQty = vQty + 1;
                                    if (isAuthenticated) updateQuantityVariantAPI(vCartItem._id, vId, vStr, vPrice, newQty);
                                    else updateQuantityVariantLocal(vId, vStr, newQty);
                                  }}
                                  disabled={vQty >= v.quantity}
                                  className="btn btn-sm p-0 border-0 text-white"
                                  style={{ width: '24px', opacity: vQty >= v.quantity ? 0.5 : 1, cursor: vQty >= v.quantity ? 'not-allowed' : 'pointer' }}
                                >
                                  <FaPlus size={10} />
                                </button>
                              </div>
                              {vQty >= v.quantity && (
                                <span className="text-danger fw-semibold mt-1" style={{ fontSize: '9px' }}>
                                  Out of stock
                                </span>
                              )}
                            </div>
                          ) : (
                            <button
                              onClick={() => handleAddVariantToCart(v, vPrice)}
                              className="btn btn-outline-success fw-bold px-3 py-1"
                              style={{
                                borderRadius: '8px', fontSize: '0.85rem',
                                color: '#10b981', borderColor: '#10b981',
                                border: '1px solid #10b981', backgroundColor: 'transparent'
                              }}
                            >
                              ADD
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-4">No variants available</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductCard;